import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ButtonV2 from '../ButtonV2';

interface ModalProps {
  isOpen?: boolean;
  title?: string;
  subtitle?: string;
  onClose: () => void;
  className?: string;
  children?: React.ReactNode;
}

const TITLE_ID = 'modal-title';

const Modal = ({ isOpen, title, subtitle, onClose, className, children }: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);

      // RGAA 7.1 — déplacer le focus sur le premier élément focusable à l'ouverture
      const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // RGAA 7.1 — piège de focus dans la modale
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return isOpen
    ? createPortal(
        // Backdrop — clic ferme la modale
        <div
          className={`Modal ${className ?? ''}`}
          onClick={onClose}
        >
          {/* RGAA 7.1 — role=dialog, aria-modal, aria-labelledby */}
          <div
            className="Modal__container"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? TITLE_ID : undefined}
            ref={modalRef}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          >
            <ButtonV2
              className="Modal__closeButton"
              leftIcon="cross"
              variant="clear"
              onClick={onClose}
              aria-label="Fermer la fenêtre"
            />
            {!!title || !!subtitle ? (
              <div className="Modal__header">
                {title ? <h4 id={TITLE_ID}>{title}</h4> : null}
                {subtitle ? <p>{subtitle}</p> : null}
              </div>
            ) : null}
            {children}
          </div>
        </div>,
        document.body
      )
    : null;
};

export default Modal;
